import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Table, Tag, Button, Space, Modal, Form, Input, Select, message } from 'antd';
import api from '../../../utils/api';

const { Title, Paragraph } = Typography;
const { Option } = Select;

const SupportRequests = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const columns = [
    { title: 'Ticket #', dataIndex: 'id', key: 'id', width: 100 },
    { title: 'Subject', dataIndex: 'subject', key: 'subject' },
    { title: 'Requester', dataIndex: 'requester', key: 'requester', width: 180 },
    { title: 'Priority', dataIndex: 'priority', key: 'priority', width: 120, render: p => <Tag color={p === 'high' ? 'red' : p === 'medium' ? 'orange' : 'blue'}>{p}</Tag> },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 130, render: s => <Tag color={s === 'open' ? 'green' : s === 'in_progress' ? 'gold' : 'default'}>{s}</Tag> },
    { title: 'Assignee', dataIndex: 'assignee', key: 'assignee', width: 160 },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at', width: 170 },
    { title: 'Actions', key: 'actions', width: 200, render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => onEdit(record)}>Update</Button>
          <Button size="small" type="primary" onClick={() => onResolve(record)}>Resolve</Button>
        </Space>
      ) }
  ];

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        setLoading(true);
        const data = await api.get('/support-tickets?limit=200');
        const items = Array.isArray(data?.items) ? data.items : [];
        const mapped = items.map(t => ({
          id: t.id,
          subject: t.subject,
          requester: t.requester_email || (t.requester_id ? `user#${t.requester_id}` : '-'),
          priority: t.priority,
          status: t.status,
          assignee: t.assignee || '-',
          created_at: t.created_at,
        }));
        if (alive) setRows(mapped);
      } catch (e) {
        console.error(e);
        message.error('Failed to load support tickets');
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, []);

  const onEdit = (record) => {
    setEditing(record);
    form.setFieldsValue(record);
    setOpen(true);
  };

  const onResolve = async (record) => {
    try {
      await api.post(`/support-tickets/${record.id}/resolve`);
      message.success(`Ticket #${record.id} marked as resolved`);
      setRows(prev => prev.map(r => r.id === record.id ? { ...r, status: 'resolved' } : r));
    } catch (e) {
      message.error(e.message || 'Failed to resolve ticket');
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await api.put(`/support-tickets/${editing.id}`, values);
      setRows(prev => prev.map(r => r.id === editing.id ? { ...editing, ...values } : r));
      setOpen(false);
      setEditing(null);
      message.success('Ticket updated');
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Admin' }, { title: 'Support' }, { title: 'Requests' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Manage Support Requests</Title>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>View and update support tickets.</Paragraph>
        <Table rowKey="id" columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 10 }} />
      </Card>

      <Modal
        open={open}
        title={editing ? `Update Ticket #${editing.id}` : 'Update Ticket'}
        onCancel={() => { setOpen(false); setEditing(null); }}
        onOk={onSubmit}
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Subject" name="subject" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Priority" name="priority" rules={[{ required: true }]}>
            <Select>
              <Option value="low">low</Option>
              <Option value="medium">medium</Option>
              <Option value="high">high</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Status" name="status" rules={[{ required: true }]}>
            <Select>
              <Option value="open">open</Option>
              <Option value="in_progress">in_progress</Option>
              <Option value="resolved">resolved</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Assignee" name="assignee">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupportRequests;
